import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Globe, 
  TrendingUp, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  Tag,
  ExternalLink
} from 'lucide-react';

// Helper function to format traffic
const formatTraffic = (num) => {
  if (!num) return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  return num.toString();
};

// Helper function to get DR color
const getDRColor = (dr) => {
  if (dr >= 70) return 'text-success';
  if (dr >= 50) return 'text-primary';
  if (dr >= 30) return 'text-warning';
  return 'text-muted-foreground';
};

// Helper function to get DR background
const getDRBg = (dr) => {
  if (dr >= 70) return 'bg-success/10 border-success/20';
  if (dr >= 50) return 'bg-primary/10 border-primary/20';
  if (dr >= 30) return 'bg-warning/10 border-warning/20';
  return 'bg-muted border-border';
};

export const ProductCard = ({ product }) => {
  const { language, t } = useLanguage();

  // Check if accepts any sensitive topic
  const acceptsSensitive = product.casino || product.cbd || product.gambling || product.cryptocurrency || product.erotic || product.sex || product.porn || product.vpn || product.tarot || product.cannabis;
  const sensitiveTopics = [];
  if (product.casino) sensitiveTopics.push(getSensitiveTopicLabel('casino', language));
  if (product.cbd) sensitiveTopics.push(getSensitiveTopicLabel('cbd', language));
  if (product.gambling) sensitiveTopics.push(getSensitiveTopicLabel('gambling', language));
  if (product.cryptocurrency) sensitiveTopics.push(getSensitiveTopicLabel('cryptocurrency', language));
  if (product.erotic) sensitiveTopics.push(getSensitiveTopicLabel('erotic', language));
  if (product.sex) sensitiveTopics.push(getSensitiveTopicLabel('sex', language));
  if (product.porn) sensitiveTopics.push(getSensitiveTopicLabel('porn', language));
  if (product.vpn) sensitiveTopics.push(getSensitiveTopicLabel('vpn', language));
  if (product.tarot) sensitiveTopics.push(getSensitiveTopicLabel('tarot', language));
  if (product.cannabis) sensitiveTopics.push(getSensitiveTopicLabel('cannabis', language));

  return (
    <Card className="group interactive-card overflow-hidden border border-border/60 bg-card hover:border-primary/30">
      <CardContent className="p-0">
        {/* Header with Domain */}
        <div className="p-5 pb-4 border-b border-border/60">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Globe className="h-4 w-4 text-primary shrink-0" />
                <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                  {product.website}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">{product.category || 'Sin categoría'}</p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg border text-center ${getDRBg(product.dr)}`}>
              <span className={`text-lg font-bold ${getDRColor(product.dr)}`}>
                {product.dr || 0}
              </span>
              <span className="text-[10px] text-muted-foreground block -mt-0.5">DR</span>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-px bg-border/60">
          {/* PVP - Precio Normal */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'PVP' : 'Price'}
            </span>
            <span className="text-xl font-bold text-accent">€{product.pvp || product.price || 0}</span>
          </div>

          {/* Premium Sale Price - Precio Sensible */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'Premium' : 'Premium'}
            </span>
            <span className="text-xl font-bold text-orange-500">€{product.premium_sale_price || product.price_sensitive || 0}</span>
          </div>

          {/* Traffic */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'Tráfico' : 'Traffic'}
            </span>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-mint-dark" />
              <span className="text-lg font-semibold text-foreground">
                {formatTraffic(product.organic_traffic)}
              </span>
            </div>
          </div>

          {/* Country */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'País' : 'Country'}
            </span>
            <span className="text-sm font-medium text-foreground truncate block">
              {product.country || '-'}
            </span>
          </div>

          {/* Link Type */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'Tipo Enlace' : 'Link Type'}
            </span>
            <span className="text-sm font-medium text-foreground">
              {product.link_type || '-'}
            </span>
          </div>

          {/* Link Duration */}
          <div className="bg-card p-4">
            <span className="text-xs text-muted-foreground block mb-1">
              {language === 'es' ? 'Duración' : 'Duration'}
            </span>
            <Badge 
              variant="secondary"
              className={`text-xs ${product.link_duration?.toLowerCase() === 'permanente' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
            >
              {product.link_duration || '-'}
            </Badge>
          </div>
        </div>

        {/* Footer Info */}
        <div className="p-4 space-y-3 bg-muted/20">
          {/* Language & Site Type */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Idioma' : 'Language'}
            </span>
            <span className="font-medium text-foreground">{product.language || '-'}</span>
          </div>

          {/* Site Type */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Tipo' : 'Type'}
            </span>
            <span className="font-medium text-foreground text-right truncate max-w-[150px]">
              {product.site_type || '-'}
            </span>
          </div>

          {/* Ad Label */}
          {product.ad_label && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {language === 'es' ? 'Etiqueta' : 'Label'}
              </span>
              <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                <Tag className="h-3 w-3 mr-1" />
                {product.ad_label}
              </Badge>
            </div>
          )}

          {/* Sensitive Topics */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {language === 'es' ? 'Sensibles' : 'Sensitive'}
            </span>
            {acceptsSensitive ? (
              <div className="flex items-center gap-1 text-success">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">
                  {language === 'es' ? 'Acepta' : 'Accepts'}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-muted-foreground">
                <XCircle className="h-3.5 w-3.5" />
                <span className="text-xs">
                  {language === 'es' ? 'No acepta' : "Doesn't accept"}
                </span>
              </div>
            )}
          </div>

          {/* Sensitive Topics Pills */}
          {sensitiveTopics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {sensitiveTopics.map((topic) => (
                <span
                  key={topic}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground"
                >
                  {topic}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="p-4 pt-0">
          <Button asChild className="w-full group/btn" variant="default">
            <Link to={`/product/${product.id}`}>
              {language === 'es' ? 'Ver Detalles' : 'View Details'}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
